import { booleanLookup } from '../../../common/constants/common';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ICmdbCiDbMsSqlInstance } from '../../../services/serviceNow/cmdbCiDbMsSqlInstance/cmdbCiDbMsSqlInstance.model';
import { IApiResponseBody, ISearchResponse } from '../../../common/models/common';
import { RootState } from '../../app.model';
import {
  deleteCmdbCiDbMsSqlInstance,
  getCmdbCiDbMsSqlInstanceById,
  searchCmdbCiDbMsSqlInstance,
  saveCmdbCiDbMsSqlInstance,
  processDataCmdbCiDbMsSqlInstance,
} from './cmdbCiDbMsSqlInstance.action';
import { ICmdbCiDbMsSqlInstanceState } from './cmdbCiDbMsSqlInstance.model';

export const initialState: ICmdbCiDbMsSqlInstanceState = {
  search: {
    loading: false,
    hasErrors: false,
    data: [],
    count: 0,
    lookups: {},
    tableName: '',
  },
  tableColumnSelection: {
    id: null,
    table_name: null,
    columns: {},
  },
  getById: {
    loading: false,
    hasErrors: false,
    data: null,
  },
  save: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  delete: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  processData: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
};

export const CmdbCiDbMsSqlInstanceSlice = createSlice({
  name: 'CmdbCiDbMsSqlInstance',
  initialState,
  reducers: {
    clearCmdbCiDbMsSqlInstance: () => {
      return initialState;
    },
    clearCmdbCiDbMsSqlInstanceMessages: (state) => {
      state.save.messages = [];
      state.delete.messages = [];
      state.processData.messages = [];
    },
    clearCmdbCiDbMsSqlInstanceGetById: (state) => {
      state.getById.data = null;
    },
    setTableColumnSelection: (state, action: PayloadAction<{ [key: string]: boolean }>) => {
      state.tableColumnSelection.columns = action.payload;
    },
  },
  extraReducers: {
    // Search
    [searchCmdbCiDbMsSqlInstance.pending.type]: (state) => {
      state.search.loading = true;
    },
    [searchCmdbCiDbMsSqlInstance.fulfilled.type]: (
      state,
      action: PayloadAction<ISearchResponse<ICmdbCiDbMsSqlInstance>>
    ) => {
      const { search_result, ...rest } = action.payload;
      state.search.data = search_result.records;
      state.search.count = search_result.total_count;
      if (JSON.stringify(rest) !== '{}') {
        state.search.lookups = { ...rest, booleanLookup };
      }
      state.search.loading = false;
      state.search.hasErrors = false;
      state.search.tableName = search_result.table_name;
      if (search_result.column_selection) {
        state.tableColumnSelection.id = search_result.column_selection.id;
        const tableSelectionObj = JSON.parse(search_result.column_selection.columns as any);
        if (tableSelectionObj.columns) {
          state.tableColumnSelection.column_orders = tableSelectionObj.column_orders;
          state.tableColumnSelection.columns = tableSelectionObj.columns;
        } else {
          state.tableColumnSelection.columns = tableSelectionObj;
        }
      }
      state.tableColumnSelection.table_name = search_result.table_name;
    },
    [searchCmdbCiDbMsSqlInstance.rejected.type]: (state) => {
      state.search.loading = false;
      state.search.hasErrors = true;
    },

    // Get by id
    [getCmdbCiDbMsSqlInstanceById.pending.type]: (state) => {
      state.getById.loading = true;
    },
    [getCmdbCiDbMsSqlInstanceById.fulfilled.type]: (state, action: PayloadAction<ICmdbCiDbMsSqlInstance>) => {
      state.getById.data = action.payload;
      state.getById.loading = false;
      state.getById.hasErrors = false;
    },
    [getCmdbCiDbMsSqlInstanceById.rejected.type]: (state) => {
      state.getById.loading = false;
      state.getById.hasErrors = true;
    },

    // Save
    [saveCmdbCiDbMsSqlInstance.pending.type]: (state) => {
      state.save.loading = true;
      state.save.messages = [];
    },
    [saveCmdbCiDbMsSqlInstance.fulfilled.type]: (state, action: PayloadAction<IApiResponseBody<unknown>>) => {
      state.save.loading = false;
      state.save.hasErrors = false;
      state.save.messages = action.payload.messages;
    },
    [saveCmdbCiDbMsSqlInstance.rejected.type]: (state) => {
      state.save.loading = false;
      state.save.hasErrors = true;
    },

    // Delete
    [deleteCmdbCiDbMsSqlInstance.pending.type]: (state) => {
      state.delete.loading = true;
      state.delete.messages = [];
    },
    [deleteCmdbCiDbMsSqlInstance.fulfilled.type]: (state, action: PayloadAction<IApiResponseBody<unknown>>) => {
      state.delete.loading = false;
      state.delete.hasErrors = false;
      state.delete.messages = action.payload.messages;
    },
    [deleteCmdbCiDbMsSqlInstance.rejected.type]: (state) => {
      state.delete.loading = false;
      state.delete.hasErrors = true;
    },

    // Process Data
    [processDataCmdbCiDbMsSqlInstance.pending.type]: (state) => {
      state.processData.loading = true;
      state.processData.messages = [];
    },
    [processDataCmdbCiDbMsSqlInstance.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.processData.loading = false;
      state.processData.hasErrors = false;
      state.processData.messages = action.payload.messages;
    },
    [processDataCmdbCiDbMsSqlInstance.rejected.type]: (state) => {
      state.processData.loading = false;
      state.processData.hasErrors = true;
    },
  },
});

// A selector
export const CmdbCiDbMsSqlInstanceSelector = (state: RootState) => state.cmdbCiDbMsSqlInstance;

// Actions
export const {
  clearCmdbCiDbMsSqlInstance,
  clearCmdbCiDbMsSqlInstanceMessages,
  clearCmdbCiDbMsSqlInstanceGetById,
  setTableColumnSelection,
} = CmdbCiDbMsSqlInstanceSlice.actions;

// The reducer
export default CmdbCiDbMsSqlInstanceSlice.reducer;
